import React, { ReactNode } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ClockCounterClockwise, Gauge, Key, Wrench } from 'phosphor-react';
import { useSearchParams } from 'react-router-dom';

import { IMcpStatus, useMcp } from '../../hooks/useMcp';
import { IDataNoPagination } from '../../types/shared';
import { AtividadeTab } from './components/AtividadeTab';
import { ChavesSessoesTab } from './components/ChavesSessoesTab';
import { FerramentasTab } from './components/FerramentasTab';
import { GuiaTab } from './components/GuiaTab';
import { VisaoGeralTab } from './components/VisaoGeralTab';
import { detalheDoErro } from './mcpUtils';
import { Cartao, Etiqueta, Pilula } from './ui';

const POLLING_MS = 15000;

type Secao = 'guia' | 'visao' | 'ferramentas' | 'acesso' | 'atividade';
const SECOES: { valor: Secao; rotulo: string; icone: ReactNode }[] = [
  { valor: 'guia', rotulo: 'Guia', icone: <BookOpen size={16} /> },
  { valor: 'visao', rotulo: 'Resumo', icone: <Gauge size={16} /> },
  { valor: 'ferramentas', rotulo: 'Ferramentas', icone: <Wrench size={16} /> },
  { valor: 'acesso', rotulo: 'Acesso', icone: <Key size={16} /> },
  { valor: 'atividade', rotulo: 'Atividade', icone: <ClockCounterClockwise size={16} /> },
];

// Aba MCP da Central de Permissões. Regra da tela: estado primeiro, pouco texto, ícone e cor
// no lugar de frase, e o detalhe só aparece quando se pede. A moldura (título e barra de
// seções da Central) vem de fora; aqui ficam as peças no mesmo padrão visual dela.
export function McpPage() {
  const { getStatus } = useMcp();
  const [params, setParams] = useSearchParams();
  const secao = SECOES.find(s => s.valor === params.get('secao'))?.valor ?? 'visao';

  const { data, isLoading, isError, error } = useQuery<IDataNoPagination<IMcpStatus>>({
    queryKey: ['mcp-status'],
    queryFn: getStatus,
    refetchOnWindowFocus: false,
    refetchInterval: POLLING_MS,
  });
  const status = data?.data;

  const ir = (novo: Secao) => {
    const p = new URLSearchParams(params);
    if (novo === 'visao') p.delete('secao');
    else p.set('secao', novo);
    p.delete('ferramenta');
    setParams(p, { replace: true });
  };

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="12px 16px" display="flex" alignItems="center" gap="14px" flexWrap="wrap">
        <Flex gap="6px" wrap="wrap" flex="1 1 auto">
          {SECOES.map(s => (
            <Pilula
              key={s.valor}
              ativa={s.valor === secao}
              onClick={() => ir(s.valor)}
              contagem={s.valor === 'ferramentas' ? status?.ferramentas.length : undefined}
            >
              {s.icone}
              {s.rotulo}
            </Pilula>
          ))}
        </Flex>
        {status && (
          <Flex align="center" gap="10px">
            <Etiqueta tom={status.config.ativo ? 'sucesso' : 'perigo'}>
              {status.config.ativo ? 'MCP ligado' : 'MCP desligado'}
            </Etiqueta>
            {status.config.escrita_ativa && <Etiqueta tom="aviso">escrita ligada</Etiqueta>}
            <Text fontSize="12px" color="#64748B">
              {status.auditoria.disponivel ? `${status.auditoria.total_24h} chamadas em 24 h` : ''}
            </Text>
          </Flex>
        )}
      </Cartao>

      {secao === 'visao' && (
        <VisaoGeralTab
          status={status}
          carregando={isLoading}
          erro={isError}
          detalheErro={detalheDoErro(error)}
        />
      )}
      {secao === 'ferramentas' && <FerramentasTab ferramentas={status?.ferramentas ?? []} />}
      {secao === 'guia' && <GuiaTab irPara={ir} />}
      {secao === 'acesso' && <ChavesSessoesTab />}
      {secao === 'atividade' && (
        <AtividadeTab
          ferramentas={status?.ferramentas ?? []}
          auditoriaDisponivel={status?.auditoria.disponivel ?? true}
        />
      )}
    </Flex>
  );
}
